import * as B from '@blueprintjs/core';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiProjectReport} from 'app/modules/Remote/Feature';

const ReportDeleteDialog: React.FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  reports: ApiProjectReport[];
  onDeleted: (count: number) => void;
  onError: (error: Error) => void;
}> = ({isOpen, onClose, projectId, reports, onDeleted, onError}) => {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (reportIds: number[]) => api.projectReports(projectId).batchArchive(reportIds),
    onSuccess: (_response, reportIds) => {
      queryClient.invalidateQueries({queryKey: ['reportCountsByFeatureId', projectId]});
      onDeleted(reportIds.length);
    },
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['projectReports', projectId]});
      onClose();
    },
  });

  // The selection is cleared on success while the dialog is still fading out,
  // so keep showing the reports it was opened with.
  const shownReportsRef = React.useRef(reports);
  if (isOpen) {
    shownReportsRef.current = reports;
  }
  const count = shownReportsRef.current.length;

  return (
    <B.Alert
      isOpen={isOpen}
      cancelButtonText="Cancel"
      confirmButtonText="Delete"
      intent={B.Intent.DANGER}
      icon="trash"
      loading={deleteMutation.isPending}
      canOutsideClickCancel
      canEscapeKeyCancel
      onCancel={onClose}
      onConfirm={() => deleteMutation.mutate(shownReportsRef.current.map((r) => r.id))}
    >
      <p>
        Are you sure you want to delete {count === 1 ? 'this report' : `these ${count} reports`}?
      </p>
      <p>
        {count === 1 ? 'It' : 'They'} will no longer be visible in Lens, so we recommend exporting
        {count === 1 ? ' it' : ' them'} to PDF first.
      </p>
    </B.Alert>
  );
};

export default ReportDeleteDialog;
